import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "error",
    "form",
    "passwordConfirmationInput",
    "passwordInput",
    "submitButton",
    "success",
    "successLink",
    "successMessage",
    "tokenInput"
  ]

  static values = {
    endpoint: String
  }

  connect() {
    this.tokenInputTarget.value = this.passwordResetToken()

    if (!this.tokenInputTarget.value) {
      this.showError("This password reset link is missing its token. Request a new reset email and try again.")
      this.setSubmitEnabled(false)
    }
  }

  async submit(event) {
    event.preventDefault()
    this.clearMessages()

    const validationError = this.validationError()

    if (validationError) {
      this.showError(validationError)
      return
    }

    this.setSubmitting(true)

    try {
      const response = await fetch(this.endpointValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin",
        body: JSON.stringify({
          password: {
            reset_password_token: this.tokenInputTarget.value,
            password: this.passwordInputTarget.value,
            password_confirmation: this.passwordConfirmationInputTarget.value
          }
        })
      })

      const body = await response.json().catch(() => ({}))

      if (!response.ok) {
        this.showError(this.errorMessage(body))
        return
      }

      this.showSuccess(body?.message || "Password has been reset successfully.")
    } catch (_error) {
      this.showError("We couldn't reset your password right now. Please try again.")
    } finally {
      this.setSubmitting(false)
    }
  }

  validationError() {
    if (!this.tokenInputTarget.value) {
      return "This password reset link is missing its token. Request a new reset email and try again."
    }

    if (!this.passwordInputTarget.value || !this.passwordConfirmationInputTarget.value) {
      return "Enter your new password in both fields."
    }

    if (this.passwordInputTarget.value !== this.passwordConfirmationInputTarget.value) {
      return "Passwords do not match."
    }

    return null
  }

  passwordResetToken() {
    const params = new URLSearchParams(window.location.search)
    return params.get("token") || params.get("reset_password_token") || ""
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  }

  setSubmitting(submitting) {
    this.submitButtonTarget.disabled = submitting
    this.submitButtonTarget.classList.toggle("loading", submitting)
    this.submitButtonTarget.classList.toggle("btn-disabled", submitting)
  }

  setSubmitEnabled(enabled) {
    this.submitButtonTarget.disabled = !enabled
    this.submitButtonTarget.classList.toggle("btn-disabled", !enabled)
  }

  clearMessages() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
    this.successTarget.classList.add("hidden")
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  showSuccess(message) {
    this.successMessageTarget.textContent = message
    this.successTarget.classList.remove("hidden")
    this.formTarget.classList.add("hidden")
  }

  errorMessage(body) {
    const details = body?.error?.details

    if (details && typeof details === "object") {
      const messages = Object.values(details).flat().filter(Boolean)

      if (messages.length > 0) {
        return messages.join(" ")
      }
    }

    return body?.error?.message || "Password could not be reset."
  }
}
